import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LocaleProvider } from "@/i18n/LocaleContext";
import useLenis from "@/hooks/useLenis";
import { Header } from "@/components/Header";
import { Hero } from "@/components/Hero";
import { DualOffering } from "@/components/DualOffering";
import { ServicesGrid } from "@/components/ServicesGrid";
import { ForBusiness } from "@/components/ForBusiness";
import { Marquee } from "@/components/Marquee";
import { Ventures } from "@/components/Ventures";
import { About } from "@/components/About";
import { Consultation } from "@/components/Consultation";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { LegalPage } from "@/components/Legal";
import { SmallBusiness } from "@/components/SmallBusiness";
import { PrivacyPage } from "@/components/Privacy";

function Page({ locale }) {
  useLenis();
  const ogImage = locale === "en" ? "/og-image.jpg" : `/og/home-${locale}.jpg`;
  return (
    <LocaleProvider locale={locale} ogImage={ogImage}>
      <div className="App bg-nowi-bg text-nowi-text font-manrope">
        <div className="noise-overlay" />
        <Header />
        <main>
          <Hero />
          <DualOffering />
          <ServicesGrid />
          <ForBusiness />
          <Marquee />
          <Ventures />
          <About />
          <Consultation />
          <Contact />
        </main>
        <Footer />
        <Toaster
          theme="dark"
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#111111",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: 0,
              color: "#F4F4F0",
              fontFamily: "Manrope, sans-serif",
            },
          }}
        />
      </div>
    </LocaleProvider>
  );
}

function RootPage() {
  const navigate = useNavigate();
  useEffect(() => {
    if (localStorage.getItem("nowi_locale_chosen")) return;
    const lang = (navigator.language || navigator.userLanguage || "").toLowerCase();
    if (lang.startsWith("sv")) navigate("/se", { replace: true });
    else if (lang.startsWith("da")) navigate("/dk", { replace: true });
    else if (lang.startsWith("nb") || lang.startsWith("nn") || lang.startsWith("no")) navigate("/no", { replace: true });
    else if (lang.startsWith("fi")) navigate("/fi", { replace: true });
    else if (lang.startsWith("is")) navigate("/is", { replace: true });
    else if (lang.startsWith("de")) navigate("/de", { replace: true });
  }, [navigate]);
  return <Page locale="en" />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootPage />} />
        <Route path="/se" element={<Page locale="sv" />} />
        <Route path="/dk" element={<Page locale="da" />} />
        <Route path="/no" element={<Page locale="nb" />} />
        <Route path="/fi" element={<Page locale="fi" />} />
        <Route path="/is" element={<Page locale="is" />} />
        <Route path="/de" element={<Page locale="de" />} />
        <Route path="/legal" element={<LegalPage locale="en" />} />
        <Route path="/se/legal" element={<LegalPage locale="sv" />} />
        <Route path="/dk/legal" element={<LegalPage locale="da" />} />
        <Route path="/no/legal" element={<LegalPage locale="nb" />} />
        <Route path="/fi/legal" element={<LegalPage locale="fi" />} />
        <Route path="/is/legal" element={<LegalPage locale="is" />} />
        <Route path="/de/legal" element={<LegalPage locale="de" />} />
        <Route path="/small-business" element={<SmallBusiness locale="en" />} />
        <Route path="/se/small-business" element={<SmallBusiness locale="sv" />} />
        <Route path="/dk/small-business" element={<SmallBusiness locale="da" />} />
        <Route path="/no/small-business" element={<SmallBusiness locale="nb" />} />
        <Route path="/fi/small-business" element={<SmallBusiness locale="fi" />} />
        <Route path="/is/small-business" element={<SmallBusiness locale="is" />} />
        <Route path="/de/small-business" element={<SmallBusiness locale="de" />} />
        <Route path="/privacy" element={<PrivacyPage locale="en" />} />
        <Route path="/se/privacy" element={<PrivacyPage locale="sv" />} />
        <Route path="/dk/privacy" element={<PrivacyPage locale="da" />} />
        <Route path="/no/privacy" element={<PrivacyPage locale="nb" />} />
        <Route path="/fi/privacy" element={<PrivacyPage locale="fi" />} />
        <Route path="/is/privacy" element={<PrivacyPage locale="is" />} />
        <Route path="/de/privacy" element={<PrivacyPage locale="de" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
